import {hasValue} from "./ValueUtils";
import {getBaseExclaimUrl} from "./BaseUrl";

let disableUnnecessaryLogging = false;

// This is used in the unit tests (test.UrlValidation.ts)
export function disableUnnecessaryLoggingForUnitTesting(): void {
    disableUnnecessaryLogging = true;
}

function protocolToPort(protocol: string): number | null {
    const p = String(protocol).toLowerCase();
    switch (p) {
        case 'http:':
            return 80;
        case 'https:':
            return 443;
        default:
            // We only care about HTTP and HTTPS for the isSafeUrl() testing
            return null;
    }
}

function urlMatchesPrefix(prefix: string, suppliedUrl: string): boolean {
    const prefixUrl = new URL(prefix);
    const urlToTest = new URL(suppliedUrl);

    const prefixPort = prefixUrl.port || protocolToPort(prefixUrl.protocol);
    const urlToTestPort = urlToTest.port || protocolToPort(urlToTest.protocol);

    return prefixUrl.protocol === urlToTest.protocol &&
        prefixUrl.hostname === urlToTest.hostname &&
        String(prefixPort) === String(urlToTestPort) &&
        prefixUrl.username === urlToTest.username &&
        prefixUrl.password === urlToTest.password &&
        urlToTest.pathname.startsWith(prefixUrl.pathname);
}

function matchesRequiredPrefix(urlAsString: string, requiredPrefix: string | string[]): boolean {
    if (Array.isArray(requiredPrefix)) {
        for (const pref of requiredPrefix) {
            if (urlMatchesPrefix(pref, urlAsString)) {
                return true;
            }
        }
        return false;
    }
    return urlMatchesPrefix(requiredPrefix, urlAsString);
}

function isValid(urlAsString: string, requiredPrefix?: string | string[]) {
    const schemeRE = /^[a-z][+\-.a-z]*:/iu;
    const schemeWithAuthorityRE = /^[a-z][+\-.a-z]*:\/{2}/iu;
    if (schemeWithAuthorityRE.test(urlAsString)) {
        // Full URI with scheme and authority at the start
        //
        // Always only allow HTTP and HTTPS prefixes (no JavaScript or other protocols)
        // noinspection HttpUrlsUsage
        if (urlAsString.startsWith("http://") || urlAsString.startsWith("https://")) {
            // If we've been provided with (a) prefix(es) to check against, check against the prefix(es)
            if (requiredPrefix) {
                return matchesRequiredPrefix(urlAsString, requiredPrefix);
            } else {
                // If not, check to see if we can compare against the base URL,
                // if not, we cannot determine it's safety, so we'll fail.
                const base = getBaseExclaimUrl();
                return hasValue(base) && urlMatchesPrefix(base, urlAsString);
            }
        }
    } else if (!schemeRE.test(urlAsString)) {
        // No URI scheme, and no authority (therefore it should be a "local" path
        //
        // Allow anything without an absolute path prefix or an authority at the start
        if (!urlAsString.startsWith("/")) {
            return true;
        }
    }
    return false;
}

export function isSafeUrl(
    url: string | null | undefined,
    requiredPrefix?: string | string[],
): url is string {
    if (!url) {
        return false;
    }

    const urlAsString = String(url);
    if (urlAsString && isValid(urlAsString, requiredPrefix)) {
        return true;
    }
    // Output a warning so that it's easier to find problems (while testing or in
    // production).
    //
    // This shouldn't happen normally or part of the regular test process, but
    // will happen in the specific unit tests for this function, so we have a hook
    // to avoid logging and making a mess of the mocha output.
    if (!disableUnnecessaryLogging) {
        console.warn("Ignoring possibly unsafe URL '%s'", urlAsString);
    }
    return false;
}

export function isValidHttpUrl(urlAsString?: string|null): urlAsString is string {
    if (!urlAsString || typeof urlAsString !== "string") {
        return false;
    }

    try {
        const url = new URL(urlAsString);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (_error) { // NOSONAR(S2486)
        return false;
    }
}
